import type { SupabaseClient } from '@supabase/supabase-js'
import type { IOrganizationRepository, Organization, OrganizationMember, Role, CreateOrganizationData } from '@knownow/domain'
import { NotFoundError } from '@knownow/domain'
import type { Database } from '@knownow/db'

type OrgRow = Database['org']['Tables']['organizations']['Row']
type MemberRow = Database['org']['Tables']['organization_members']['Row']

export class SupabaseOrganizationRepository implements IOrganizationRepository {
  constructor(private readonly client: SupabaseClient<Database>) {}

  async getById(id: string): Promise<Organization> {
    const { data, error } = await this.client
      .schema('org')
      .from('organizations')
      .select('*')
      .eq('id', id)
      .single()

    if (error !== null || data === null) {
      throw new NotFoundError('Organization', id)
    }

    return this.orgToDomain(data)
  }

  async getBySlug(slug: string): Promise<Organization> {
    const { data, error } = await this.client
      .schema('org')
      .from('organizations')
      .select('*')
      .eq('slug', slug)
      .single()

    if (error !== null || data === null) {
      throw new NotFoundError('Organization', slug)
    }

    return this.orgToDomain(data)
  }

  async create(data: CreateOrganizationData): Promise<Organization> {
    // Use SECURITY DEFINER function to atomically create org + owner membership.
    // This avoids the RLS chicken-and-egg: the SELECT policy on organizations
    // requires membership, but membership can't exist before the org does.
    const { data: rows, error } = await this.client
      .schema('org')
      .rpc('create_organization_with_owner', {
        p_name: data.name,
        p_slug: data.slug,
        p_preferred_language: data.preferredLanguage ?? 'en',
      })

    if (error !== null) {
      throw error
    }

    const row = Array.isArray(rows) ? rows[0] : rows
    if (!row) {
      throw new Error('Failed to create organization')
    }

    return this.orgToDomain(row as unknown as OrgRow)
  }

  async getMembers(organizationId: string): Promise<readonly OrganizationMember[]> {
    const { data, error } = await this.client
      .schema('org')
      .from('organization_members')
      .select('*')
      .eq('organization_id', organizationId)

    if (error !== null) {
      throw error
    }

    return (data ?? []).map(this.memberToDomain)
  }

  async getMembersByUserId(userId: string): Promise<readonly OrganizationMember[]> {
    const { data, error } = await this.client
      .schema('org')
      .from('organization_members')
      .select('*')
      .eq('user_id', userId)

    if (error !== null) {
      throw error
    }

    return (data ?? []).map(this.memberToDomain)
  }

  async addMember(member: OrganizationMember): Promise<void> {
    const { error } = await this.client
      .schema('org')
      .from('organization_members')
      .insert({
        user_id: member.userId,
        organization_id: member.organizationId,
        role: member.role,
        created_at: member.createdAt.toISOString(),
      })

    if (error !== null) {
      throw error
    }
  }

  async removeMember(userId: string, organizationId: string): Promise<void> {
    const { error } = await this.client
      .schema('org')
      .from('organization_members')
      .delete()
      .eq('user_id', userId)
      .eq('organization_id', organizationId)

    if (error !== null) {
      throw error
    }
  }

  async updateMemberRole(userId: string, organizationId: string, role: Role): Promise<void> {
    const { error } = await this.client
      .schema('org')
      .from('organization_members')
      .update({ role })
      .eq('user_id', userId)
      .eq('organization_id', organizationId)

    if (error !== null) {
      throw error
    }
  }

  private orgToDomain(row: OrgRow): Organization {
    return {
      id: row.id,
      name: row.name,
      slug: row.slug,
      createdBy: row.created_by,
      preferredLanguage: row.preferred_language ?? 'en',
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    }
  }

  private memberToDomain(row: MemberRow): OrganizationMember {
    return {
      userId: row.user_id,
      organizationId: row.organization_id,
      role: row.role as Role,
      createdAt: new Date(row.created_at),
    }
  }
}
